/**
 * Accessible tabs primitive (FRIDAY-65).
 *
 * The repo had no tab widget, so this is the one place that owns the ARIA
 * contract every settings screen shares:
 *
 *   - `role="tablist"` on the strip, `role="tab"` on each button.
 *   - `aria-selected` marks the active tab, `aria-controls` points at the
 *     panel the tab owns.
 *   - `role="tabpanel"` + `aria-labelledby` on the panel, so a screen reader
 *     announces the panel with the name of its tab.
 *   - Roving tabindex: only the selected tab is in the tab order, the rest
 *     are reached with the arrow keys.
 *   - ArrowLeft / ArrowRight move between tabs, Home / End jump to the first
 *     and last one. Selection follows focus (the standard automatic
 *     activation pattern).
 *
 * The widget is router-agnostic: it renders the strip and reports the
 * selected id. The settings shell turns that into a navigation.
 */

import { useRef, type KeyboardEvent, type ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface TabItem {
  id: string
  label: string
  icon?: LucideIcon
  disabled?: boolean
}

export interface TabsProps {
  tabs: TabItem[]
  /** The id of the selected tab. */
  activeId: string
  /** Called with the id of the tab the user picked. */
  onSelect: (id: string) => void
  /** Accessible name of the tab strip. */
  label?: string
  /** Prefix for the generated tab/panel ids. */
  idBase?: string
  className?: string
}

export function Tabs({
  tabs,
  activeId,
  onSelect,
  label = 'Sections',
  idBase = 'tab',
  className,
}: TabsProps) {
  const buttons = useRef(new Map<string, HTMLButtonElement>())

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End']
    if (!keys.includes(event.key)) return
    const current = tabs.findIndex((t) => t.id === activeId)
    if (current < 0) return
    event.preventDefault()
    let next = current
    if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length
    else if (event.key === 'ArrowRight') next = (current + 1) % tabs.length
    else if (event.key === 'Home') next = 0
    else next = tabs.length - 1
    const target = tabs[next]
    if (!target || target.disabled) return
    onSelect(target.id)
    // The button already exists, so the focus can move before the route
    // change lands — no flash of an unfocused tab strip.
    buttons.current.get(target.id)?.focus()
  }

  return (
    <>
      <div
        role="tablist"
        aria-label={label}
        onKeyDown={onKeyDown}
        className={cn(
          'border-surface-200 dark:border-surface-700 flex gap-1 overflow-x-auto border-b',
          className,
        )}
      >
        {tabs.map((tab) => {
          const selected = tab.id === activeId
          const Icon = tab.icon
          return (
            <button
              key={tab.id}
              ref={(el) => {
                if (el) buttons.current.set(tab.id, el)
                else buttons.current.delete(tab.id)
              }}
              type="button"
              role="tab"
              id={`${idBase}-tab-${tab.id}`}
              aria-selected={selected}
              aria-controls={`${idBase}-panel-${tab.id}`}
              tabIndex={selected ? 0 : -1}
              disabled={tab.disabled}
              onClick={() => onSelect(tab.id)}
              className={cn(
                'focus-visible:ring-brand-500 -mb-px flex min-h-11 shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium',
                'focus-visible:ring-2 focus-visible:outline-none',
                selected
                  ? 'border-brand-500 text-brand-600 dark:text-brand-300'
                  : 'border-transparent text-surface-500 hover:text-surface-700 dark:hover:text-surface-300',
                tab.disabled && 'cursor-not-allowed opacity-50',
              )}
            >
              {Icon && <Icon size={14} aria-hidden="true" />}
              {tab.label}
            </button>
          )
        })}
      </div>
      {/* The panel of an inactive tab is rendered by another route, so its id
          is reserved here to keep every `aria-controls` resolvable. */}
      {tabs
        .filter((t) => t.id !== activeId)
        .map((t) => (
          <div
            key={t.id}
            role="tabpanel"
            id={`${idBase}-panel-${t.id}`}
            aria-labelledby={`${idBase}-tab-${t.id}`}
            hidden
          />
        ))}
    </>
  )
}

export interface TabPanelProps {
  /** The id of the tab this panel belongs to. */
  id: string
  idBase?: string
  className?: string
  children: ReactNode
}

export function TabPanel({ id, idBase = 'tab', className, children }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      id={`${idBase}-panel-${id}`}
      aria-labelledby={`${idBase}-tab-${id}`}
      tabIndex={0}
      className={cn('focus-visible:ring-brand-500 focus-visible:ring-2 focus-visible:outline-none', className)}
    >
      {children}
    </div>
  )
}
